import { OvertimeUnitTypeOptions, ThirdPartyCost } from '@app/core/model/third-party-cost.model'

import { Money } from '@app/features/scope-overview/model/money.model'
import { Preference } from '@app/core/model/user-preferences.interface'
import { TableColumnKey } from '../../ui-components/scope-ui-table/table-column-key.enum'
import { ThirdPartyCostFormula } from '@app/core/model/third-party-cost-formula.model'
import { isNil } from 'lodash'
import { plainToInstance } from 'class-transformer'

export const getThirdPartyCostColumns = (
  isEditable?: boolean,
  onChange?: (tpc: ThirdPartyCost) => void
): Preference[] => [
  {
    key: TableColumnKey.TPC_NAME,
    name: 'THIRD PARTY COST',
    selected: true,
    isVisibleInList: () => true,
    field: 'name',
  },
  {
    key: TableColumnKey.TPC_AMOUNT,
    name: 'TPC Quantity',
    selected: true,
    field: 'quantity',
    isVisibleInList: () => isEditable,
    inputType: () => 'number',
    inputContext: { isDecimal: false },
    onInputChange: (tpc: ThirdPartyCost, value) => {
      if (Number.isNaN(value) || value == null) return
      const updatedTpc = plainToInstance(ThirdPartyCost, {
        ...tpc,
        quantity: value,
        formula: plainToInstance(ThirdPartyCostFormula, { ...tpc.formula, quantity: value }),
      })
      onChange(updatedTpc)
    },
  },
  { key: TableColumnKey.UNIT, name: 'Unit', selected: true, isVisibleInList: () => true, field: 'formula.subjectName' },
  {
    key: TableColumnKey.QUANTITY,
    name: 'Unit Quantity',
    selected: true,
    isEditable: () => isEditable,
    isVisibleInList: (tpc: ThirdPartyCost) => tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.unitTypes?.length,
    value: (tpc: ThirdPartyCost) =>
      tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.unitTypes?.length
        ? tpc.formula?.unitTypeQuantity || 1
        : '-',
    inputType: () => 'number',
    inputContext: { isDecimal: false },
    onInputChange: (tpc: ThirdPartyCost, value) => {
      if (Number.isNaN(value) || value == null) return
      const updatedTpc = plainToInstance(ThirdPartyCost, {
        ...tpc,
        formula: plainToInstance(ThirdPartyCostFormula, { ...tpc.formula, unitTypeQuantity: value }),
      })
      onChange(updatedTpc)
    },
  },
  {
    key: TableColumnKey.UNIT_TYPE,
    name: 'Unit type',
    selected: true,
    isVisibleInList: () => true,
    value: (tpc: ThirdPartyCost) =>
      tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.unitTypes?.length ? tpc.formula?.unitType?.name : '-',
    isDisabled: ({ entity }) =>
      !(isEditable && entity.formula?.thirdPartyCostFormulaDefinitionSnapshot?.unitTypes?.length),
    optionsFunction: (tpc: ThirdPartyCost) =>
      tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.unitTypes?.length
        ? ['-'].concat(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.unitTypes?.map((type) => type.name))
        : ['-'],
    hideOptionsMenu: () => false,
    optionDisplayFn: (option: string) => option,
    onChange: ({ element, value }) => {
      const tpc = getTpcWithUpdatedUnitType(element, value)
      onChange(tpc)
    },
  },
  {
    key: TableColumnKey.UNIT_COST,
    name: 'Unit cost',
    selected: true,
    isVisibleInList: () => true,
    field: 'cost',
    isDisabled: () => !isEditable,
    inputType: () => 'editable-number',
    inputContext: { isDecimal: true, formClass: 'wide' },
    onInputChange: (tpc: ThirdPartyCost, value) => {
      if (Number.isNaN(value) || value == null) return
      const updatedTpc = plainToInstance(ThirdPartyCost, { ...tpc, cost: new Money(value, tpc.cost.currency) })
      onChange(updatedTpc)
    },
  },
  {
    key: TableColumnKey.OVERTIME_QUANTITY,
    name: 'Overtime quantity',
    selected: true,
    isEditable: () => isEditable,
    isVisibleInList: (tpc: ThirdPartyCost) =>
      !isNil(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount),
    value: (tpc: ThirdPartyCost) =>
      !isNil(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount)
        ? tpc.formula?.overtimeUnitQuantity || 0
        : '-',
    inputType: () => 'number',
    inputContext: { isDecimal: false },
    onInputChange: (tpc: ThirdPartyCost, value) => {
      if (Number.isNaN(value) || value == null) return
      const updatedTpc = plainToInstance(ThirdPartyCost, {
        ...tpc,
        formula: plainToInstance(ThirdPartyCostFormula, { ...tpc.formula, overtimeUnitQuantity: value }),
      })
      onChange(updatedTpc)
    },
  },
  {
    key: TableColumnKey.OVERTIME_UNIT_TYPE,
    name: 'Overtime unit type',
    selected: true,
    isVisibleInList: (tpc: ThirdPartyCost) =>
      !isNil(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount),
    value: (tpc: ThirdPartyCost) =>
      !isNil(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount)
        ? OvertimeUnitTypeOptions.find((t) => t.id == tpc.formula?.overtimeUnitType)?.name
        : '-',
    isDisabled: ({ entity }) =>
      !isEditable || isNil(entity.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount),
    optionDisplayFn: (option: string) => option,
    optionsFunction: (tpc: ThirdPartyCost) =>
      !isNil(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount)
        ? OvertimeUnitTypeOptions.map((option) => option.name)
        : ['-'],
    onChange: ({ element, value }) => {
      const updatedTpc = plainToInstance(ThirdPartyCost, {
        ...element,
        formula: plainToInstance(ThirdPartyCostFormula, {
          ...element.formula,
          overtimeUnitType: OvertimeUnitTypeOptions.find((t) => t.name === value)?.id,
        }),
      })
      onChange(updatedTpc)
    },
  },
  {
    key: TableColumnKey.OVERTIME_RATE,
    name: 'Overtime rate',
    selected: true,
    isVisibleInList: (tpc: ThirdPartyCost) =>
      !isNil(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount),
    value: (tpc: ThirdPartyCost) =>
      !isNil(tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount)
        ? new Money(tpc.formula?.overtimeRate, tpc.cost.currency)
        : null,
    isDisabled: ({ entity }) =>
      !isEditable || isNil(entity.formula?.thirdPartyCostFormulaDefinitionSnapshot?.includeAddAmount),
    inputType: () => 'editable-number',
    inputContext: { isDecimal: true, formClass: 'wide' },
    onInputChange: (tpc: ThirdPartyCost, value) => {
      if (Number.isNaN(value) || value == null) return
      const updatedTpc = plainToInstance(ThirdPartyCost, {
        ...tpc,
        formula: plainToInstance(ThirdPartyCostFormula, { ...tpc.formula, overtimeRate: value }),
      })
      onChange(updatedTpc)
    },
  },
  {
    key: TableColumnKey.OVERTIME_TOTAL,
    name: 'Overtime total',
    selected: true,
    isVisibleInList: () => true,
    value: (thirdPartyCost: ThirdPartyCost) =>
      !isNil(thirdPartyCost.formula?.overtimeRate) ? getTotalAddAmountWithCurrency(thirdPartyCost) : '-',
  },
  {
    key: TableColumnKey.MARK_UP,
    name: 'Markup',
    selected: true,
    field: 'markupPercentageDecimal',
    isVisibleInList: () => true,
    isDisabled: () => !isEditable,
    inputType: () => 'editable-number',
    inputContext: { isDecimal: true, formClass: 'wide' },
    onInputChange: (tpc: ThirdPartyCost, value) => {
      if (Number.isNaN(value) || value == null) return
      const updatedTpc = plainToInstance(ThirdPartyCost, {
        ...tpc,
        markupPercentageDecimal: value,
        newMarkupPercentage: value,
        markupPercentage: tpc.markupValue(value),
      })
      onChange(updatedTpc)
    },
  },
  {
    key: TableColumnKey.TPC_UNIT_PRICE,
    name: 'Unit price',
    selected: true,
    isVisibleInList: () => true,
    value: (thirdPartyCost: ThirdPartyCost) => thirdPartyCost.calculateUnitSellingPrice(),
    isDisabled: () => true,
    inputType: () => 'editable-number',
    inputContext: { isDecimal: true, formClass: 'wide' },
  },
  {
    key: TableColumnKey.SELLING_PRICE,
    name: 'Selling price',
    selected: true,
    isVisibleInList: () => true,
    value: (thirdPartyCost: ThirdPartyCost) => thirdPartyCost.calculateSellingPrice(),
    isDisabled: () => !isEditable,
    inputType: () => 'editable-number',
    inputContext: { isDecimal: true, formClass: 'wide' },
    onInputChange: (tpc: ThirdPartyCost, value) => {
      if (Number.isNaN(value) || value == null) return
      const updatedTpc = plainToInstance(ThirdPartyCost, {
        ...tpc,
        newSellingPrice: value,
        markupPercentageDecimal: tpc.getMarkUpBySellingPrice(value),
      })
      onChange(updatedTpc)
    },
  },
]

const findConversion = (tpc: ThirdPartyCost, from: number, to: number) =>
  tpc.formula.getUnitTypeConvertors.find((c) => c.fromUnitType === from && c.toUnitType === to)

function getTotalAddAmountWithCurrency(tpc: ThirdPartyCost): Money {
  let overtimeRate = null
  if (tpc.formula)
    overtimeRate = tpc.formula.overtimeRate ? tpc.formula.overtimeRate * tpc.formula.overtimeUnitQuantity : 0
  return new Money(overtimeRate, tpc.cost.currency)
}

export function getTpcWithUpdatedUnitType(tpc: ThirdPartyCost, value: string) {
  const type = tpc.formula?.thirdPartyCostFormulaDefinitionSnapshot?.unitTypes?.find((t) => t.name === value)
  const edit = { ...tpc }
  const editFormula = { ...edit.formula }

  if (edit.formula?.unitType) {
    const from = tpc.formula.unitType.id
    const to = editFormula.unitType.id
    const conversion = findConversion(tpc, from, to)
    if (conversion) {
      edit.cost = new Money(edit.cost.amount * conversion.value, edit.cost.currency)
    } else {
      const reverseConversion = findConversion(tpc, to, from)
      if (reverseConversion) edit.cost = new Money(edit.cost.amount / reverseConversion.value, edit.cost.currency)
    }
  }

  const formula = { ...edit.formula, unitType: type }
  edit.formula = plainToInstance(ThirdPartyCostFormula, formula)

  return plainToInstance(ThirdPartyCost, edit)
}
